"use client";
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { VirtuosoGrid } from "react-virtuoso";
import type { CategoryNode, ItemCardDTO } from "@/types/pim";
import { getItemSlugFromNameAndId } from "@/types/pim";
import { useCatalogParams } from "../hooks/useCatalogParams";
import ItemCard from "./InventoryList.ItemCard";
import { CATALOG_STATE_KEY, clearCatalogState, getCatalogState, urlsMatchIgnoringParams, type CatalogState } from "@/types/catalog-state";
import { usePublishCatalogResults } from "./InventoryList.ResultsContext";

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

interface CatalogGridProps {
  initialItems: ItemCardDTO[];
  initialTotal: number;
  initialPageSize: number;
  totalPages: number;
  currentPage: number | null;
  initialCursor: string | undefined;
  selectedCategory?: CategoryNode | null;
  searchQuery?: string;
  /** Slimmed to the two fields the card-link fallback reads. */
  rootCategories: { CategoryId: number; CategorySlug: string }[];
  restore?: boolean;
}

/**
 * The items-dependent half of the catalog view. Streams in inside the page's
 * Suspense boundary while the shell (toolbars, sidebar, filters) is already
 * interactive. Publishes result counts to CatalogResultsContext so the shell's
 * count displays can fill in once the data arrives.
 */
const CatalogGrid: React.FC<CatalogGridProps> = ({
  initialItems,
  initialTotal,
  initialPageSize,
  totalPages,
  currentPage,
  initialCursor,
  selectedCategory,
  searchQuery,
  rootCategories,
  restore = false,
}) => {
  // State - only track additionally loaded items
  const [additionalItems, setAdditionalItems] = useState<ItemCardDTO[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [cursor, setCursor] = useState<string | undefined>(initialCursor);
  const pageSize = initialPageSize;

  const { attributeFilters } = useCatalogParams();
  // Publish-only channel with stable identity: subscribing to it never
  // re-renders the grid when counts change, so VirtuosoGrid doesn't recompute
  // (and remount/flicker card images) on every publish or shell render.
  const publish = usePublishCatalogResults();

  // Derive combined items and hasMore from props + loaded items
  const items = useMemo(() => {
    const combined = [...initialItems, ...additionalItems];

    // Validate for duplicate items in development
    if (process.env.NODE_ENV === 'development') {
      const itemIds = combined.map((item) => item.id);
      const duplicateIds = itemIds.filter((id, index) => itemIds.indexOf(id) !== index);

      if (duplicateIds.length > 0) {
        const uniqueDuplicates = [...new Set(duplicateIds)];
        console.error(`❌ [CatalogGrid] Found ${duplicateIds.length} duplicate item(s) in items memo!`);
        console.error(`   Duplicate item IDs: ${uniqueDuplicates.join(', ')}`);
      }
    }

    return combined;
  }, [initialItems, additionalItems]);

  const hasMore = items.length < initialTotal;

  // Surface the live counts to the shell (sidebar count line, mobile strip,
  // mobile filter menu) which renders outside the Suspense boundary.
  useEffect(() => {
    publish({ loadedCount: items.length, total: initialTotal, pageSize, currentPage });
  }, [publish, items.length, initialTotal, pageSize, currentPage]);

  // Save catalog state to session storage before navigating to a product page
  const saveCatalogState = useCallback(() => {
    const catalogState: CatalogState = {
      url: window.location.href,
      scrollPosition: window.scrollY,
      itemsLoaded: items.length,
    };
    sessionStorage.setItem(CATALOG_STATE_KEY, JSON.stringify(catalogState));
  }, [items.length]);

  // Reset additional items when initial data changes (e.g., filters/category change)
  useEffect(() => {
    setAdditionalItems([]);
    setCursor(initialCursor);
  }, [initialItems, initialCursor]);

  useEffect(() => {
    const catalogState = getCatalogState();
    if (restore && catalogState && urlsMatchIgnoringParams(catalogState.url, window.location.href)) {
      // Restore scroll position after items are loaded
      setTimeout(() => {
        window.scrollTo(0, catalogState.scrollPosition);
      }, 100); // Small delay to let items render

      // Clean up URL params
      const url = new URL(window.location.href);
      url.searchParams.delete('loaded');
      url.searchParams.delete('restore');
      window.history.replaceState(window.history.state, '', url.toString());
    }
    clearCatalogState();
  }, [restore]);

  // Load more items (for infinite scroll)
  const loadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore || !cursor) {
      return;
    }

    setIsLoadingMore(true);

    try {
      const params = new URLSearchParams({
        pageSize: pageSize.toString(),
      });

      if (cursor) {
        params.set('cursor', cursor);
      }

      if (selectedCategory?.Id) {
        params.set('categoryId', selectedCategory?.Id.toString());
      }

      if (selectedCategory?.isRoot) {
        params.set('isRootCategory', 'true');
      }

      if (selectedCategory?.Path) {
        params.set('categoryPath', selectedCategory.Path);
      }

      if (selectedCategory?.RootCategoryId) {
        params.set('rootCategoryId', selectedCategory.RootCategoryId.toString());
      }

      if (searchQuery) params.set('search', searchQuery);

      if (Object.keys(attributeFilters).length > 0) {
        params.set('attributeFilters', JSON.stringify(attributeFilters));
      }

      const response = await fetch(`/api/items?${params}`);
      if (!response.ok) throw new Error('Failed to load more items');

      const data = await response.json();

      setAdditionalItems(prev => [...prev, ...data.items]);
      setCursor(data.cursor);
    } catch (error) {
      console.error('🟣 [CatalogGrid] ❌ Failed to load more items:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, hasMore, cursor, pageSize, searchQuery, attributeFilters, selectedCategory]);

  return (
    <VirtuosoGrid
      style={{ height: '100svh' }}
      useWindowScroll
      initialItemCount={items.length >= 30 ? 30 : items.length}
      // Keep a few screens mounted above and below the viewport so cards near
      // the edges don't unmount/remount on small scrolls — each remount makes
      // the Next.js <Image> re-fetch+decode, which flickers over the remote CDN.
      overscan={{ main: 1200, reverse: 1200 }}
      // Key Virtuoso's item wrappers by stable item identity (not the default
      // index) so appending pages (load-more) and the skeleton swap don't
      // recycle a wrapper onto a different item and reload its image.
      computeItemKey={(index, item) => ('isSkeleton' in item ? item.id : items[index].GPID)}
      context={{ isLoadingMore, loadMore, hasMore, itemsLength: items?.length || 0, totalItems: initialTotal || 0, totalPages }}
      data={[
        ...items,
        ...(isLoadingMore ? Array.from({ length: initialTotal - items.length >= 30 ? 30 : initialTotal - items.length }, (_, i) => ({ isSkeleton: true, id: `skeleton-${i}` } as any)) : [])
      ]}
      listClassName="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 sm:gap-7 lg:gap-8 px-4 lg:px-8"
      itemContent={(index, item) => {
        return (
          <ItemCard
            key={'isSkeleton' in item ? item.id : items[index].GPID}
            item={item}
            index={index}
            itemPageSlug={'isSkeleton' in item ? '#' : `${item.rootCategorySlug || rootCategories?.find(c => c.CategoryId === item.categoryId)?.CategorySlug || ''}/${getItemSlugFromNameAndId(item.Name, item.Id) || ""}`}
            onBeforeNavigate={saveCatalogState}
          />
        );
      }}
      components={{ Footer }}
    />
  );
};

const PaginationNav: React.FC<{ totalPages: number; crawlerOnly: boolean }> = ({ totalPages, crawlerOnly }) => {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const currentPage = Math.max(1, Number(searchParams.get('page')) || 1);

  // Helper to build page link
  const buildPageLink = (page: number) => {
    const params = new URLSearchParams(searchParams.toString());
    if (page === 1) {
      params.delete('page');
    } else {
      params.set('page', page.toString());
    }
    return `${pathname}?${params.toString()}`.replace(/\?$/, '');
  };

  // Generate page numbers with ellipsis logic
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 4) {
        pages.push(1, 2, 3, 4, 5, '...', totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }
    return pages;
  };

  const paginationStyle: React.CSSProperties = crawlerOnly ?
    {
      position: 'absolute',
      width: '1px',
      height: '1px',
      padding: 0,
      margin: '-1px',
      overflow: 'hidden',
      clipPath: 'inset(50%)',
      whiteSpace: 'nowrap',
      borderWidth: 0,
    } :
    {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      marginTop: '1.5rem',
      userSelect: 'none',
    };

  return (
    <nav style={paginationStyle} aria-label="Pagination">
      {/* Previous */}
      {currentPage > 1 && (
        <a
          rel="prev"
          href={buildPageLink(currentPage - 1)}
          className="px-3 py-1 rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 transition-colors"
        >
          &lt; Previous
        </a>
      )}

      {/* Page numbers */}
      {getPageNumbers().map((page, idx) =>
        typeof page === 'number' ? (
          <a
            key={page}
            href={buildPageLink(page)}
            aria-current={page === currentPage ? 'page' : undefined}
            className={`px-3 py-1 rounded border text-sm font-medium transition-colors ${page === currentPage
              ? 'bg-gcopper text-white border-gcopper cursor-default pointer-events-none'
              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
              }`}
          >
            {page}
          </a>
        ) : (
          <span key={`ellipsis-${idx}`} className="px-2 text-gray-400 select-none">…</span>
        )
      )}

      {/* Next */}
      {currentPage < totalPages && (
        <a
          rel="next"
          href={buildPageLink(currentPage + 1)}
          className="px-3 py-1 rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 transition-colors"
        >
          Next &gt;
        </a>
      )}
    </nav>
  );
};

interface FooterProps {
  context: {
    loadMore: () => void;
    isLoadingMore: boolean;
    hasMore: boolean;
    itemsLength: number;
    totalItems: number;
    totalPages: number;
  };
}

const Footer: React.FC<FooterProps> = ({ context: { loadMore, isLoadingMore, hasMore, itemsLength, totalItems, totalPages } }) => {
  const searchParams = useSearchParams();

  return (
    <div
      style={{
        backgroundColor: 'transparent',
        padding: '2rem',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        marginTop: '3rem',
      }}
    >
      {
        totalItems === 0 ? (
          <div className="text-center py-12 text-gray-400 px-4">
            <svg className="mx-auto h-12 w-12 text-gray-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
            <p className="text-lg font-medium">No items found</p>
            <p className="text-sm mt-1">Try adjusting your filters</p>
          </div>
        )
          : searchParams.get('page') != null ? (
            <PaginationNav totalPages={totalPages} crawlerOnly={false} />
          ) : (
            <>
              {
                hasMore ? (
                  <span className="text-neutral-600">Showing {itemsLength} results</span>
                ) : (
                  <span className="text-neutral-600">Showing all results (<span style={{ fontWeight: 'bold' }}>{itemsLength}</span>)</span>
                )
              }

              <div className="flex w-full justify-center gap-4 p-4">
                <button onClick={scrollToTop} style={{ backgroundColor: 'white', color: 'black', paddingTop: 6, paddingBottom: 6, paddingLeft: 10, paddingRight: 10, borderRadius: 20, border: '1px solid gray', cursor: isLoadingMore ? 'not-allowed' : 'pointer' }}>
                  Back to top
                </button>
                {
                  hasMore && (
                    <button disabled={isLoadingMore || !hasMore} onClick={loadMore} style={{ backgroundColor: 'white', color: 'black', paddingTop: 6, paddingBottom: 6, paddingLeft: 10, paddingRight: 10, borderRadius: 20, border: '1px solid gray', cursor: isLoadingMore ? 'not-allowed' : 'pointer' }}>
                      {isLoadingMore ? 'Loading...' : 'Load more'}
                    </button>
                  )
                }
              </div>

              <PaginationNav totalPages={totalPages} crawlerOnly={true} />
            </>
          )

      }

    </div>

  )
}

export default CatalogGrid;
